// frontend/src/pages/club/ClubFinancials/ClubFinancials.jsx
import React, { useEffect, useState, useCallback } from 'react';
import StatCard from '../../../components/common/StatCard';
import Pagination from '../../../components/common/Pagination';
import { getTransactionsApi, getFinancialSummaryApi } from '../../../api/financial.api';
import { TRANS_TYPE_LABELS, TRANS_STATUS_LABELS, formatCurrency } from '../../../utils/constants';

export default function ClubFinancials() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ type: '', status: '', from: '', to: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const [txRes, sumRes] = await Promise.all([
        getTransactionsApi({ ...clean, page: pagination.page, limit: pagination.limit }),
        getFinancialSummaryApi(clean),
      ]);
      setTransactions(txRes.data.data);
      setPagination(prev => ({ ...prev, total: txRes.data.meta.total, totalPages: txRes.data.meta.totalPages }));
      setSummary(sumRes.data.data);
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت اطلاعات مالی');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">وضعیت مالی باشگاه</h1>

      {summary && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard title="مجموع درآمد" value={formatCurrency(summary.totalIncome)} color="green" />
          <StatCard title="مجموع هزینه" value={formatCurrency(summary.totalExpense)} color="red" />
          <StatCard title="تراز" value={formatCurrency(summary.balance)} color={summary.balance >= 0 ? 'green' : 'red'} />
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        >
          <option value="">همه انواع</option>
          {Object.entries(TRANS_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">همه وضعیت‌ ها</option>
          {Object.entries(TRANS_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
        />
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 overflow-x-auto">
        {loading ? (
          <div className="py-8 text-center text-text-dark/50">در حال بارگذاری...</div>
        ) : (
          <>
            <table className="w-full text-right text-sm">
              <thead className="bg-slate-50 text-text-dark/70">
                <tr>
                  <th className="px-4 py-3 font-medium">تاریخ</th>
                  <th className="px-4 py-3 font-medium">نوع</th>
                  <th className="px-4 py-3 font-medium">دسته</th>
                  <th className="px-4 py-3 text-left font-medium">مبلغ</th>
                  <th className="px-4 py-3 font-medium">وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3">{new Date(t.date).toLocaleDateString('fa-IR')}</td>
                    <td className="px-4 py-3">{TRANS_TYPE_LABELS[t.type]}</td>
                    <td className="px-4 py-3">{t.category}</td>
                    <td className="px-4 py-3 text-left font-mono">{formatCurrency(t.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                        t.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700' :
                        t.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {TRANS_STATUS_LABELS[t.status]}
                      </span>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-text-dark/40">
                      تراکنشی ثبت نشده است
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}